import { IAddress, ICartItem, IStripeSessionResponse } from "./cart";
import {
  IAddOnOption,
  IProduct,
  IProductAddOn,
  IProductCategory,
} from "./products";
import { IShopSettings } from "./shopSettings";
import { IDeliveryDriver } from "./delivery";
import { IOrderTypeDetails } from "./order";

export interface IRootStore {
  authStore: IAuthStore;
  productStore: IProductStore;
  addOnStore: IAddOnStore;
  addOnOptstore: IAddOnOptionStore;
  cartStore: ICartStore;
  userStore: IUserStore;
  showLoading: (message?: string) => void;
  hideLoading: () => void;
}

export interface IAuthStore {
  login: Function;
  register: Function;
  forgotPassword: Function;
  resetPassword: Function;
}

export interface IProductStore {
  products: Array<IProduct>;
  fetchProducts: Function;
  fetchProductById: Function;
  editProduct: IProduct | null;
  setEditProduct: Function;
  categories: Array<IProductCategory>;
  selectedCategoryId: number;
  setSelectedCategoryId: Function;
  saveProduct: Function;
  deleteProduct: Function;
  hideProduct: Function;
  fetchProductCategories: Function;
  addOns: Array<IProductAddOn>;
  fetchAddOns: Function;
  saveProductAddOns: Function;
  isAddOnsRequest: boolean;
}

export interface IAddOnStore {
  addOns: Array<IProductAddOn>;
  editAddOn: IProductAddOn | null;
  fetchAddOns: Function;
  setEditAddOn: Function;
  saveAddOn: Function;
  deleteAddOn: Function;
}

export interface IAddOnOptionStore {
  addOnOptions: Array<IAddOnOption>;
  editAddOnOption: IAddOnOption | null;
  fetchAddOnOptions: Function;
  setEditAddOnOption: Function;
  saveAddOnOption: Function;
  deleteAddOnOption: Function;
}

export interface ICartStore {
  cartItems: Array<ICartItem>;
  cartItemsCount: number;
  addItemToCart: Function;
  getCartItem: Function;
  deliveryMethod: DeliveryMethod;
  setDeliveryMethod: Function;
  clearCartItems: Function;
  updateCartItemQuantity: Function;
  validateCartItems: Function;
  paymentMethod: PaymentMethod;
  deliveryAddress?: IAddress;
  orderDateTime: {
    date: string;
    time: string;
  };
  deliveryPostcode: string;
  setDeliveryAddress: Function;
  setOrderDateTime: Function;
  setPaymentMethod: (method: PaymentMethod) => void;
  cartTotal: number;
  setCartTotal: Function;
  deliveryCost: number;
  tableNumber: number;
  setTableNumber: (tableNo: number) => void;
  setDeliveryCost: Function;
  placeOrder: Function;
  getOrderTypeDetails: Function<IOrderTypeDetails>;
  storeOrderTypeDetails: Function;
  setOrderTypeDetailsFromStorage: Function;
  checkDistance: Function;
  voucherDiscount: number;
  setVoucherDiscount: Function;
  setAppliedVoucherCode: Function;
}

export interface IOrderStore {
  orders: Array<IOrder>;
  viewOrder: IOrder | null;
  fetchOrders: Function;
  receiveNewOrders: Function;
  fetchCustomerOrders: Function;
  fetchOrderById: Function;
  updateOrderStatus: Function;
  markAsPaymentPaid: Function;
  takePayment: Function;
  refreshPaymentStatus: (orderId: number, id: string,
    callback?: (response: ApiResponse<IStripeSessionResponse> | undefined) => void
  ) => Promise<ApiResponse<IStripeSessionResponse> | undefined>;
  createRefund: Function;
  refreshRefundStatus: Function;
  assignDeliveryDriver: Function;
  updateOrderPrintStatus: Function;
}

export interface IUserStore {
  users: Array<IUserCommand>;
  editUser: IUserCommand | null;
  fetchUsers: Function;
  createUser: Function;
  updateUser: Function;
  deleteUser: Function;
  setEditUser: Function;
  userAddress?: IAddress;
  getUserAddress: Function;
  saveUserProfile: Function;
  changePassword: Function;
  getUserDetails: Function;
  userDetails: IUserCommand | null;
  changePasswordByUserId: Function;
}

export interface IShopSettingsStore {
  shopSettings: IShopSettings;
  getShopSettings: Function;
  updateShopSettings: Function;
}

export interface IDeliveryStore {
  drivers: Array<IDeliveryDriver>;
  editDriver: IDeliveryDriver | null;
  fetchDrivers: Function;
  setEditDriver: Function;
  saveDriver: Function;
  deleteDriver: Function;
}

export interface IAllergenStore {
  allergens: Array<IAllergen>;
  editAllergen: IAllergen | null;
  fetchAllergens: Function;
  setEditAllergen: Function;
  saveAllergen: Function;
  deleteAllergen: Function;
}

export interface ICategoryStore {
  categories: Array<IProductCategory>;
  fetchCategories: Function;
  editCategory: IProductCategory | null;
  setEditCategory: Function;
  saveCategory: Function;
  deleteCategory: Function;
}

export interface IProductOfferStore {
  offers: Array<IProductOffer>;
  fetchOffers: Function;
  liveOffers: Array<IProductOffer>;
  fetchLiveOffers: Function;
  offerTypes: Array<IOfferType>;
  deleteOffer: Function;
  editOffer: IProductOffer | null;
  setEditOffer: Function;
  saveOffer: Function;
  receiveNewOffers: Function;
}

export interface IVoucherStore {
  vouchers: Array<IVoucher>;
  fetchVouchers: Function;
  liveVouchers: Array<IVoucher>;
  fetchLiveVouchers: Function;
  discountTypes: Array<VoucherDiscountType>;
  deleteVoucher: Function;
  editVoucher: IVoucher | null;
  setEditVoucher: Function;
  saveVoucher: Function;
  applyVoucher: Function;
  receiveNewVouchers: Function;
  shareVoucherWithCustomers: Function;
  shareVoucherWithAdmin: Function;
  previewVoucherEmail: Function;
  previewEmailBody: string | null;
  setPreviewEmailBody: Function;
}

export interface IModal {
  show: boolean;
  setShowModal: (show: boolean) => void;
}

export interface ILoginModal extends IModal {
  loginCallback?: () => void;
}

export type SelectOption = {
  label: string;
  value: string;
};

export interface IAuthContext {
  isScrolledDown: boolean;
  isMobileResolution: boolean;
  isAuthenticated: boolean;
  userId?: string;
  userName: string;
  firstName?: string;
  surname?: string;
  isShopAdmin: boolean;
  isShopUser: boolean;
  isShopCustomer: boolean;
  setAuth: Function;
  clearAuth: Function;
}

export interface IShopContext extends IShopSettings {}

export type ApiResponse<T> = {
  message: string;
  data: T;
  isSuccess: boolean;
};

export enum DeliveryMethod {
  Delivery = 1,
  Collection = 2,
  DineIn = 3,
}

export enum PaymentMethod {
  Cash = 1,
  Card = 2,
  Direct = 3,
}

export enum PaymentStatus {
  Pending = 1,
  Paid = 2,
}

export enum OrderStatus {
  Placed = 1,
  InProgress = 2,
  OutForDelivery = 3,
  ReadyForCollection = 4,
  Cancelled = 5,
  ReturnRequested = 6,
  ReturnApproved = 7,
  Returned = 8,
  Completed = 9,
  RefundInitiated = 10,
  Refunded = 11,
}

export enum StripeRefundStatusEnum {
  Pending = 1,
  RequiresAction = 2,
  Succeeded = 3,
  Failed = 4,
  Canceled = 5,
}

export enum OfferTypeEnum {
  Discount = 1,
  Buy1Get1 = 2,
  FreeProduct = 3,
  MultiBuys = 4,
}

export enum UserRoleEnum {
  Customer = "Shop.Customer",
  Admin = "Shop.Admin",
  ShopUser = "Shop.User",
  SuperAdmin = "SuperAdmin",
}
